if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (this.dataset.addedToCart === 'true') {
          window.location = window.routes.cart_url;
          return;
        }

        if (this.dataset.maxLimit && window.MaxCartQuantity) {
          this.checkMaxCartQuantity().then((allowed) => {
            if (allowed) this.submitToCart(evt);
          });
          return;
        }

        this.submitToCart(evt);
      }

      // Per-product cap from the `custom.max_cart_quantity` metafield. Resolves false (and shows the message)
      // when the add would push the product past its limit. If /cart.js can't be read, the add goes ahead and
      // Shopify's own rules decide.
      async checkMaxCartQuantity() {
        const limit = parseInt(this.dataset.maxLimit);
        const quantity = parseInt(new FormData(this.form).get('quantity')) || 1;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        let existing;
        try {
          existing = await window.MaxCartQuantity.getProductCartQuantity(this.dataset.productId);
        } catch (e) {
          console.error(e);
          return true;
        } finally {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
        }

        if (existing + quantity <= limit) return true;

        const message = window.MaxCartQuantity.message(limit, this.dataset.productTitle, existing);
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-form',
          productVariantId: this.variantIdInput?.value,
          errors: message,
          message,
        });
        const inlineError = !this.hideErrors && this.querySelector('.product-form__error-message-wrapper');
        if (inlineError) {
          this.handleErrorMessage(message);
        } else {
          window.MaxCartQuantity.toast(message);
        }
        return false;
      }

      submitToCart(evt) {
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              // Any rejected add (422 inventory/quantity limits, etc.) surfaces Shopify's own message and
              // leaves the button usable; it must not be read as "sold out".
              this.handleErrorMessage(response.description || response.message);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: response,
            }).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            });

            // Product cards opt in via data-view-cart-label: after a successful add the button links to the cart.
            if (this.dataset.viewCartLabel) {
              this.dataset.addedToCart = 'true';
              this.submitButtonText.textContent = this.dataset.viewCartLabel;
              this.submitButton.removeAttribute('aria-haspopup');
            }

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings?.error);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      // Per-product cap reached (cart already holds the full allowance). Called by product-info whenever the
      // cart count changes, so it also resets when items are removed in the drawer or cart page.
      setLimitReached(reached, limit) {
        this.dataset.limitReached = reached ? 'true' : 'false';

        const message = this.querySelector('.product-form__limit-message');
        if (message) {
          message.hidden = !reached;
          message.textContent = reached
            ? window.variantStrings.limitReachedMessage.replace('[LIMIT]', limit)
            : '';
        }

        const paymentButton = this.querySelector('.shopify-payment-button');
        if (paymentButton) paymentButton.toggleAttribute('inert', reached);

        // Re-enable only if this limit is what disabled the button (not sold out / unavailable).
        const limitDisabled = this.submitButton.hasAttribute('data-limit-disabled');
        if (reached ? !this.submitButton.hasAttribute('disabled') || limitDisabled : limitDisabled) {
          this.toggleSubmitButton(false);
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!disable && this.dataset.limitReached === 'true') {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('data-limit-disabled', '');
          this.submitButtonText.textContent = window.variantStrings.limitReached;
          return;
        }
        if (!disable || text) this.submitButton.removeAttribute('data-limit-disabled');

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
